import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getApiErrorMessage, getChatConversations, getModels, getMyBillingUsage } from '../../api'
import { EmptyState, LoadingBlock, MetricCard, Section, StatusPill } from '../../components/v2/V2Primitives'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import { asList, compactNumber, hasConnectedProvider, modelTitle, providerLinks, setupChecklist } from './v2Utils'

export default function AdminV2Dashboard() {
  const { fanvueReviewWorkspace: fanvueOnlyWorkspace } = useWorkspaceScope()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [models, setModels] = useState([])
  const [conversations, setConversations] = useState([])
  const [billing, setBilling] = useState(null)

  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true)
      setError('')
      const [modelsResult, chatsResult, billingResult] = await Promise.allSettled([
        getModels(),
        getChatConversations(null, false, { limit: 12, offset: 0 }),
        getMyBillingUsage(),
      ])
      if (!alive) return
      if (modelsResult.status === 'fulfilled') setModels(asList(modelsResult.value))
      else setError(getApiErrorMessage(modelsResult.reason, 'Failed to load models'))
      if (chatsResult.status === 'fulfilled') setConversations(asList(chatsResult.value))
      if (billingResult.status === 'fulfilled') setBilling(billingResult.value)
      setLoading(false)
    }
    load()
    return () => { alive = false }
  }, [])

  const queues = useMemo(() => {
    const needsSetup = models.filter((model) => setupChecklist(model).some((item) => !item.done))
    const connectionIssues = models.filter((model) => !hasConnectedProvider(model))
    const syncRunning = models.filter((model) => ['running', 'syncing', 'queued', 'in_progress'].includes(String(model.media_sync_status || '').toLowerCase()))
    const syncFailed = models.filter((model) => String(model.media_sync_status || '').toLowerCase() === 'failed')
    const ready = models.filter((model) => setupChecklist(model).every((item) => item.done))
    return { needsSetup, connectionIssues, syncRunning, syncFailed, ready }
  }, [models])
  const meteredBilling = billing && ['per_bot_message', 'hybrid'].includes(String(billing.plan || '').toLowerCase()) && Number(billing.rate_cents || 0) > 0

  if (loading) return <LoadingBlock />

  return (
    <div className="v2-stack">
      {error ? <div className="v2-error">{error}</div> : null}

      <div className="v2-metrics-grid v2-dashboard-metrics-grid">
        <MetricCard label="Models" value={compactNumber(models.length)} detail="Total visible models" />
        <MetricCard label="Ready" value={compactNumber(queues.ready.length)} detail="Checklist completed" tone="good" />
        <MetricCard label="Need setup" value={compactNumber(queues.needsSetup.length)} detail="Basic settings missing" tone="warn" />
        <MetricCard label="Active inbox" value={compactNumber(conversations.length)} detail="Recent conversations" />
        {billing ? (
          <>
            {meteredBilling ? <MetricCard
              label="Balance"
              value={formatCents(billing.balance_cents, billing.currency)}
              detail={billing.billing_paused ? 'Sending paused by balance' : `${compactNumber(billing.available_messages)} messages available`}
              tone={billing.billing_paused ? 'bad' : 'good'}
            /> : <MetricCard
              label="Plan"
              value={planLabel(billing.plan)}
              detail={fanvueOnlyWorkspace ? 'Managed through the Fanvue App Store' : 'No per-message charge'}
              tone="good"
            />}
            <MetricCard
              label="Sent 30d"
              value={compactNumber(billing.outgoing_bot_messages_30d ?? billing.billable_bot_messages_30d ?? 0)}
              detail={`${compactNumber(billing.billable_bot_messages_30d ?? 0)} billable messages`}
            />
            {meteredBilling ? <MetricCard
              label="Spend 30d"
              value={formatCents(billing.charged_amount_30d_cents ?? billing.estimated_amount_30d_cents, billing.currency)}
              detail={`${formatCents(billing.rate_cents, billing.currency)} per bot message`}
            /> : null}
          </>
        ) : null}
      </div>

      <div className="v2-dashboard-grid">
        <Section eyebrow="Work queues" title="What needs attention now" description="Operator-first queues replace generic dashboard cards.">
          <div className="v2-work-queue-list">
            <QueueCard title="Connection attention" tone="bad" items={queues.connectionIssues} empty="All connected accounts look healthy." />
            <QueueCard title="Setup incomplete" tone="warn" items={queues.needsSetup} empty="All models have required setup." />
            <QueueCard title="Sync running" tone="neutral" items={queues.syncRunning} empty="No active sync jobs." />
            <QueueCard title="Sync failed" tone="bad" items={queues.syncFailed} empty="No failed media syncs." />
          </div>
        </Section>

        <Section eyebrow="Workflow" title="How the workspace is organized" description="Move from connection and setup to inbox operations and automation control.">
          <div className="v2-flow-map">
            {['Connections', 'Models', 'Content sources', 'Inbox', 'Automation', 'Diagnostics'].map((item, index) => (
              <div key={item} className="v2-flow-step">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{item}</strong>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  )
}

function formatCents(cents, currency = 'USD') {
  const value = Number(cents || 0) / 100
  return `${value.toFixed(2)} ${currency || 'USD'}`
}

function planLabel(plan) {
  const value = String(plan || 'monthly_flat').toLowerCase()
  if (value === 'monthly_flat') return 'Monthly'
  if (value === 'revenue_share') return 'Revenue share'
  if (value === 'per_bot_message') return 'Per message'
  if (value === 'hybrid') return 'Hybrid'
  return 'Custom'
}

function QueueCard({ title, tone, items, empty }) {
  return (
    <article className="v2-queue-card">
      <div className="v2-queue-head">
        <strong>{title}</strong>
        <StatusPill tone={tone}>{items.length}</StatusPill>
      </div>
      {items.length ? (
        <ul className="v2-queue-items">
          {items.slice(0, 4).map((model) => (
            <li key={model.id}>
              <Link to={`/v2/models/${model.id}`}>{modelTitle(model)}</Link>
              <span>{providerLinks(model).length || 0} connection slots</span>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={empty} />
      )}
    </article>
  )
}
